import { CommonModule, formatDate, formatNumber, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Action, ActionStore, ActionbarComponent, ActionbarMode } from '@igo2/common/action';
import { EntityRecord, EntityStore, EntityTableTemplate, getEntityTitle } from '@igo2/common/entity';
import { PanelComponent } from '@igo2/common/panel';
import { StopPropagationDirective } from '@igo2/common/stop-propagation';
import { ConfigService } from '@igo2/core/config';
import { LanguageService } from '@igo2/core/language';
import { Media, MediaService } from '@igo2/core/media';
import { StorageScope, StorageService, StorageServiceEvent } from '@igo2/core/storage';
import { AnyLayer, Feature, FeatureDetailsComponent, FeatureMotion, GeoServiceDefinition, IgoMap, Layer, LayerService, PropertyTypeDetectorService, SearchResult, SearchResultsComponent, WMSDataSource, computeOlFeaturesExtent, featureFromOl, featureToOl, featuresAreOutOfView, featuresAreTooDeepInView, generateIdFromSourceOptions, getCommonVectorSelectedStyle, getCommonVectorStyle, isLayerItem, moveToOlFeatures } from '@igo2/geo';
import { QueryState, StorageState, WorkspaceState } from '@igo2/integration';
import { ObjectUtils } from '@igo2/utils';

import olFeature from 'ol/Feature';
import olFormatGeoJSON from 'ol/format/GeoJSON';
import olPoint from 'ol/geom/Point';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { debounceTime, map, skipWhile, tap } from 'rxjs/operators';
import { ZzzSecuriteService } from 'src/app/zzz/securite/shared/securite.service';
import { CommentairePlansTravailService } from 'src/app/shared/services/commentaire-plans-travail.service';
import { CommentaireGeneralService } from 'src/app/shared/services/commentaire-general.service';
import { InscrireCommentaireService } from 'src/app/integration/sigt-inscrire-commentaire/sigt-inscrire-commentaire.service';
import { MessageService } from '@igo2/core/message';
import { Constantes } from 'src/app/shared/constantes';
import { RdeContext, RechercheRdeService } from 'src/app/shared/services/recherche-rde.service';
import { DroitContrainteDto } from 'src/app/shared/models/droit-contraint.dto';
import { TransactionProprieteAmendementDto } from 'src/app/shared/models/transaction-propriete-amendement.dto';

interface ExtendedGeoServiceDefinition extends GeoServiceDefinition {
  propertyForUrl: string;
}
interface RdeSearchLayer {wmsLayer:string, rdeSearchType:string, sourceField:string|string[]};

@Component({
    selector: 'app-toast-panel',
    templateUrl: './toast-panel.component.html',
    styleUrls: ['./toast-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TranslateModule,
        MatBadgeModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        NgTemplateOutlet,
        PanelComponent,
        ActionbarComponent,
        FeatureDetailsComponent,
        SearchResultsComponent,
        StopPropagationDirective
    ]
})
export class ToastPanelComponent implements OnInit, OnDestroy {
  static SWIPE_ACTION = {
    RIGHT: 'swiperight',
    LEFT: 'swipeleft',
    UP: 'swipeup',
    DOWN: 'swipedown'
  };

  public tabsMode: boolean;
  private locale: string;
  isLayerItem = isLayerItem;

  get storageService(): StorageService {
    return this.storageState.storageService;
  }

  @Input()
  get map(): IgoMap {
    return this._map;
  }
  set map(value: IgoMap) {
    this._map = value;
  }
  private _map: IgoMap;

  @Input()
  get store(): EntityStore<SearchResult<Feature>> {
    return this._store;
  }
  set store(value: EntityStore<SearchResult<Feature>>) {
    this._store = value;
    this.resultSelected$.subscribe(f => f && this.loadActions());
    this.store.entities$.subscribe((_entities) => {
      this.loadActions();
      this.unselectResult();
    });
  }
  private _store: EntityStore<SearchResult<Feature>>;

  @Input()
  get opened(): boolean {
    return this._opened;
  }
  set opened(value: boolean) {
    if (value !== !this._opened) {
      return;
    }
    this._opened = value;
    this.storageService.set('toastOpened', value, StorageScope.SESSION);
    this.openedChange.emit(value);
  }
  private _opened = true;

  @Input() hasFeatureEmphasisOnSelection = false;

  get zoomAuto(): boolean {
    return this._zoomAuto;
  }
  set zoomAuto(value) {
    if (value !== !this._zoomAuto) {
      return;
    }
    this._zoomAuto = value;
    this.zoomAuto$.next(value);
    this.storageService.set('zoomAuto', value);
  }
  private _zoomAuto = false;

  // To allow the toast to use much larger extent on the map
  get fullExtent(): boolean {
    return this._fullExtent;
  }
  set fullExtent(value) {
    if (value !== !this._fullExtent) {
      return;
    }
    this._fullExtent = value;
    this.fullExtent$.next(value);
    this.fullExtentEvent.emit(value);
    this.storageService.set('fullExtent', value);
  }
  private _fullExtent = false;

  public potententialLayerToAdd$ = new BehaviorSubject<any>(undefined);
  public potententialLayerisAdded$ = new BehaviorSubject<boolean>(false);

  public fullExtent$ = new BehaviorSubject<boolean>(this.fullExtent);
  public isHtmlDisplay = false;
  public iconResizeWindows = '';

  public icon = 'menu';

  public actionStore = new ActionStore([]);
  public actionbarMode = ActionbarMode.Overlay;

  private multiple$ = new BehaviorSubject(false);
  private isResultSelected$ = new BehaviorSubject(false);
  public isSelectedResultOutOfView$ = new BehaviorSubject(false);
  private isSelectedResultOutOfView$$: Subscription;
  private storageChange$$: Subscription;
  private initialized = true;

  private format = new olFormatGeoJSON();

  private resultOrResolution$$: Subscription;
  private focusedResult$ = new BehaviorSubject<SearchResult<Feature>>(undefined);
  private abstractFocusedOrSelectedResult: Feature;

  private messageApplication: { [key: string]: string } = {};

  private isSuppressionCommentaireTravailPermis = false;
  private isSuppressionCommentaireGeneralPermis = false;
  private isSuppressionCommentaireOfficielPermis = false;
  private layerCommentairePlansOfficiel = "CommentairesPlansOfficielsPoints";
  private layerCommentairePlansTravail = "CommentairesPlansTravailPoints";
  private layerConsultations = "Consultations";

  private rdeSearchLayers: RdeSearchLayer[];

  public withZoomButton = true;
  zoomAuto$ = new BehaviorSubject<boolean>(false);

  @Output() openedChange = new EventEmitter<boolean>();

  @Output() fullExtentEvent = new EventEmitter<boolean>();
  @Output() windowHtmlDisplayEvent = new EventEmitter<boolean>();

  resultSelected$ = new BehaviorSubject<SearchResult<Feature>>(undefined);

  // @HostBinding('class.app-toast-panel-opened')
  // get hasOpenedClass() {
  //   return this.opened;
  // }

  // @HostBinding('class.app-full-toast-panel-collapsed')
  // get hasFullCollapsedClass() {
  //   return !this.opened && this.fullExtent;
  // }

  getClassPanel() {
    return {
      'app-toast-panel-opened':
        this.opened && !this.fullExtent && !this.isHtmlDisplay,
      'app-full-toast-panel-opened':
        this.opened && this.fullExtent && !this.isHtmlDisplay,

      'app-toast-panel-html':
        this.opened &&
        !this.fullExtent &&
        this.resultSelected$.value &&
        this.isHtmlDisplay,

      'app-toast-panel-html-large':
        this.opened &&
        this.fullExtent &&
        this.resultSelected$.value &&
        this.isHtmlDisplay,

      'app-toast-panel-collapsed':
        !this.opened && !this.fullExtent && !this.isHtmlDisplay,
      'app-full-toast-panel-collapsed':
        !this.opened && this.fullExtent && !this.isHtmlDisplay,
      'app-toast-panel-html-collapsed': !this.opened && this.isHtmlDisplay
    };
  }

  // if query tabs mode activated
  // fix Heigh of igo-panel
  setHeighPanelTabsMode() {
    if (this.resultSelected$.value || !this.opened) {
      return '';
    }

    if (this.tabsMode && !this.fullExtent && !this.isHtmlDisplay) {
      return 'app-toast-panel-opened-max-height';
    } else if (
      this.tabsMode &&
      this.opened &&
      this.fullExtent &&
      !this.isHtmlDisplay
    ) {
      return 'app-full-toast-panel-opened-max-height';
    }
  }

  @HostBinding('style.visibility')
  get displayStyle() {
    if (this.results.length) {
      if (this.results.length === 1 && this.initialized) {
        this.selectResult(this.results[0]);
      }
      return 'visible';
    }
    return 'hidden';
  }

  // @HostBinding('class.app-full-toast-panel-opened')
  // get hasFullOpenedClass() {
  //   return this.opened && this.fullExtent;
  // }

  @HostListener('document:keydown.escape', ['$event']) onEscapeHandler() {
    this.clear();
  }

  @HostListener('document:keydown.backspace', ['$event']) onBackHandler() {
    this.unselectResult();
  }

  @HostListener('document:keydown.z', ['$event']) onZoomHandler() {
    if (this.isResultSelected$.getValue() === true) {
      const localOlFeature = this.format.readFeature(
        this.resultSelected$.getValue().data,
        {
          dataProjection: this.resultSelected$.getValue().data.projection,
          featureProjection: this.map.projection
        }
      ) as olFeature;
      moveToOlFeatures(
        this.map.viewController,
        [localOlFeature],
        FeatureMotion.Default
      );
    }
  }

  get isRdeSearchAllowed() {
    //search result must be from one of the RDE layers
    const searchResult = this.resultSelected$.getValue();
    if (searchResult) {
      const rdeLayer = this.getRdeLayerInfo(searchResult);
      return (rdeLayer !== undefined);
    }
    return false;
  }

  get results(): SearchResult<Feature>[] {
    // return this.store.view.filter((e) => e.meta.dataType === FEATURE).all();
    return this.store.all();
  }

  get multiple(): Observable<boolean> {
    this.results.length
      ? this.multiple$.next(true)
      : this.multiple$.next(false);
    return this.multiple$;
  }

  constructor(
    public mediaService: MediaService,
    public languageService: LanguageService,
    private storageState: StorageState,
    private queryState: QueryState,
    private workspaceState: WorkspaceState,
    private configService: ConfigService,
    private propertyTypeDetectorService: PropertyTypeDetectorService,
    private layerService: LayerService,
    private zzzSecuriteService: ZzzSecuriteService,
    private commentairePlansTravailService: CommentairePlansTravailService,
    private commentaireGeneralService: CommentaireGeneralService,
    private commentaireOfficielService: InscrireCommentaireService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private rechercheRdeService : RechercheRdeService,
    private cdRef: ChangeDetectorRef
  ) {
    this.tabsMode = this.configService.getConfig('queryTabs', false);
    this.opened = this.storageService.get('toastOpened') as boolean;
    this.zoomAuto = this.storageService.get('zoomAuto') as boolean;
    this.fullExtent = this.storageService.get('fullExtent') as boolean;
    this.setResizeWindowIcon();
    this.locale = this.languageService.getLanguage() + "-" + this.translateService.getBrowserCultureLang().split("-")[1];
    this.rdeSearchLayers = this.configService.getConfig("rdeSearch")?.layers;
  }

  private monitorResultOutOfView() {
    this.isSelectedResultOutOfView$$ = combineLatest([
      this.map.viewController.state$,
      this.resultSelected$
    ])
      .pipe(debounceTime(100))
      .subscribe((bunch) => {
        const selectedResult = bunch[1];
        if (!selectedResult) {
          this.isSelectedResultOutOfView$.next(false);
          return;
        }
        const selectedOlFeature = featureToOl(
          selectedResult.data,
          this.map.projection
        );
        const selectedOlFeatureExtent = computeOlFeaturesExtent(
          [selectedOlFeature],
          this.map.viewProjection
        );
        this.isSelectedResultOutOfView$.next(
          featuresAreOutOfView(this.map.getExtent(), selectedOlFeatureExtent)
        );
      });
  }

  ngOnInit() {
    this.store.entities$.subscribe(() => {
      this.initialized = true;
    });
    this.monitorResultOutOfView();

    let latestResult;
    let trigger;
    if (this.hasFeatureEmphasisOnSelection) {
      this.resultOrResolution$$ = combineLatest([
        this.focusedResult$.pipe(
          tap((res) => {
            latestResult = res;
            trigger = 'focused';
          })
        ),
        this.resultSelected$.pipe(
          tap((res) => {
            latestResult = res;
            trigger = 'selected';
          })
        ),
        this.map.viewController.resolution$,
        this.store.entities$
      ]).subscribe(() => this.buildResultEmphasis(latestResult, trigger));
    }

    this.storageChange$$ = this.storageService.storageChange$
      .pipe(
        skipWhile(
          (storageChange: StorageServiceEvent) =>
            storageChange.key !== 'zoomAuto'
        )
      )
      .subscribe((change) => {
        this.zoomAuto = change.currentValue;
      });

      this.computeFeatureGeoServiceStatus();
    combineLatest([
      this.resultSelected$,
      this.map.layerController.all$ as BehaviorSubject<Layer[]>
    ]).subscribe(() => {
      this.computeFeatureGeoServiceStatus();
    });

    this.isSuppressionCommentaireTravailPermis = this.zzzSecuriteService
      .utilisateurEstAuthentifie
      ? this.zzzSecuriteService.hasRole(
        Constantes.Roles.RU_GESTI_TERRI_PUBLI_SIG
      )
      : false;

    this.isSuppressionCommentaireGeneralPermis = this.zzzSecuriteService
      .utilisateurEstAuthentifie
      ? this.zzzSecuriteService.hasRole(
        Constantes.Roles.RU_GESTI_TERRI_PUBLI_SIG
      )
      : false;

    this.isSuppressionCommentaireOfficielPermis = this.zzzSecuriteService
      .utilisateurEstAuthentifie
      ? this.zzzSecuriteService.hasRole(
        Constantes.Roles.RU_GESTI_TERRI_PUBLI_SIG
      )
      : false;

    this.messageApplication.operationSucces = Constantes.MessageApplicatif.ElementEnregistrer_7I;
    this.messageApplication.confirmerSuppression = Constantes.MessageApplicatif.SupprimerElement_9A.replace("{0}", '');
  }

  private loadActions() {
    this.actionStore.load([
      {
        id: 'list',
        title: this.languageService.translate.instant('toastPanel.backToList'),
        icon: 'list',
        tooltip: this.languageService.translate.instant(
          'toastPanel.listButton'
        ),
        display: () => {
          return this.isResultSelected$;
        },
        handler: () => {
          this.unselectResult();
        }
      },
      {
        id: 'sigtSuppressionCommentaireTravail',
        title: this.languageService.translate.instant('toastPanel.delete'),
        icon: 'delete-forever-outline',
        tooltip: this.languageService.translate.instant(
          'toastPanel.delete'
        ),
        display: () => {
          if (this.isSuppressionCommentaireTravailPermis) {
            const coucheCommentaires = this.map.layerController.all.filter(f => this.isLayerItem(f) && f.dataSource.options?.type == 'wms'
              && (f.dataSource as WMSDataSource).params.LAYERS == this.layerCommentairePlansTravail
              && new RegExp('^' + f.options.title).test(this.resultSelected$.getValue()?.meta.title));
            return new BehaviorSubject(coucheCommentaires.length > 0);
          } else return new BehaviorSubject(false);
        },
        handler: () => {
          if (window.confirm(this.messageApplication.confirmerSuppression)) {
            this.commentairePlansTravailService.supprimerCommentaire(
              this.resultSelected$.getValue().data.properties.NUMERO_COMMENTAIRE as number
            )
              .subscribe(f => {
                if (f.estSucces) {
                  (<Layer> this.findLayerByName(this.layerCommentairePlansTravail)).ol.getSource().refresh();
                  this.messageService.info(this.messageApplication.operationSucces);
                  this.clear();
                } else {
                  console.error("Erreur: ", f.messages);
                }
              });
          }
        }
      },
      {
        id: 'sigtSuppressionCommentaireGeneral',
        title: this.languageService.translate.instant('toastPanel.delete'),
        icon: 'delete-forever-outline',
        tooltip: this.languageService.translate.instant(
          'toastPanel.delete'
        ),
        display: () => {
          if (this.isSuppressionCommentaireGeneralPermis) {
            const coucheCommentaires = this.map.layerController.all.filter(f => this.isLayerItem(f) && f.dataSource.options?.type == 'wms'
              && (f.dataSource as WMSDataSource).params.LAYERS == this.layerConsultations
              && new RegExp('^' + f.options.title).test(this.resultSelected$.getValue()?.meta.title));
            return new BehaviorSubject(coucheCommentaires.length > 0);
          } else return new BehaviorSubject(false);
        },
        handler: () => {
          if (window.confirm(this.messageApplication.confirmerSuppression)) {
            this.commentaireGeneralService.supprimerCommentaire(
              this.resultSelected$.getValue().data.properties.NUMERO_COMMENTAIRE as number
            )
              .subscribe(f => {
                if (f.estSucces) {
                  (<Layer> this.findLayerByName(this.layerConsultations)).ol.getSource().refresh();
                  this.messageService.info(this.messageApplication.operationSucces);
                  this.clear();
                } else {
                  console.error("Erreur: ", f.messages);
                }
              });
          }
        }
      },
      {
        id: 'sigtSuppressionCommentaireOfficiel',
        title: this.languageService.translate.instant('toastPanel.delete'),
        icon: 'delete-forever-outline',
        tooltip: this.languageService.translate.instant(
          'toastPanel.delete'
        ),
        display: () => {
          if (this.isSuppressionCommentaireOfficielPermis) {
            const coucheCommentaires = this.map.layerController.all.filter(f => this.isLayerItem(f) && f.dataSource.options?.type == 'wms'
              && (f.dataSource as WMSDataSource).params.LAYERS == this.layerCommentairePlansOfficiel
              && new RegExp('^' + f.options.title).test(this.resultSelected$.getValue()?.meta.title));
            return new BehaviorSubject(coucheCommentaires.length > 0);
          } else return new BehaviorSubject(false);
        },
        handler: () => {
          if (window.confirm(this.messageApplication.confirmerSuppression)) {
            this.commentaireOfficielService.supprimerCommentaire(
              this.resultSelected$.getValue().data.properties.NUMERO_COMMENTAIRE as number
            )
              .subscribe(f => {
                if (f.estSucces) {
                  (<Layer> this.findLayerByName(this.layerCommentairePlansOfficiel)).ol.getSource().refresh();
                  this.messageService.info(this.messageApplication.operationSucces);
                  this.clear();
                } else {
                  console.error("Erreur: ", f.messages);
                }
              });
          }
        }
      },
      {
        id: 'sigtRechercheRde',
        title: this.languageService.translate.instant('toastPanel.rdeSearch'),
        icon: 'feature_search-feature_search-outline',
        tooltip: this.languageService.translate.instant(
          'toastPanel.rdeSearch'
        ),
        display: () => {
          if (this.isRdeSearchAllowed) {
           return new BehaviorSubject(true);
          } else return new BehaviorSubject(false);
        },
        handler: () => {
          const searchResult = this.resultSelected$.getValue();
          const layer = this.getRdeLayerInfo(searchResult);
          if(!layer) return;

          let key:string = null;
          if (typeof layer.sourceField === "string") {
            key  = searchResult.data.properties[layer.sourceField];
          } else if (Array.isArray(layer.sourceField)) {
            key = searchResult.data.properties[layer.sourceField[1]] ||
                  searchResult.data.properties[layer.sourceField[0]];
          }
          this.rechercherRde(layer, key);
        }
      },
      {
        id: 'zoomFeature',
        title: this.languageService.translate.instant(
          'toastPanel.zoomOnFeature'
        ),
        icon: 'zoom_in',
        tooltip: this.languageService.translate.instant(
          'toastPanel.zoomOnFeatureTooltip'
        ),
        display: () => {
          return this.isResultSelected$;
        },
        handler: () => {
          const localOlFeature = this.format.readFeature(
            this.resultSelected$.getValue().data,
            {
              dataProjection: this.resultSelected$.getValue().data.projection,
              featureProjection: this.map.projection
            }
          ) as olFeature;
          moveToOlFeatures(
            this.map.viewController,
            [localOlFeature],
            FeatureMotion.Zoom
          );
        }
      },
      {
        id: 'zoomResults',
        title: this.languageService.translate.instant(
          'toastPanel.zoomOnFeatures'
        ),
        tooltip: this.languageService.translate.instant(
          'toastPanel.zoomOnFeaturesTooltip'
        ),
        icon: 'frame_inspect',
        availability: () => {
          return this.multiple;
        },
        handler: () => {
          const olFeatures = [];
          for (const result of this.store.all()) {
            const localOlFeature = this.format.readFeature(result.data, {
              dataProjection: result.data.projection,
              featureProjection: this.map.projection
            }) as olFeature;
            olFeatures.push(localOlFeature);
          }
          moveToOlFeatures(
            this.map.viewController,
            olFeatures,
            FeatureMotion.Zoom
          );
        }
      },
      {
        id: 'zoomAuto',
        title: this.languageService.translate.instant('toastPanel.zoomAuto'),
        tooltip: this.languageService.translate.instant(
          'toastPanel.zoomAutoTooltip'
        ),
        checkbox: true,
        checkCondition: this.zoomAuto$,
        handler: () => {
          this.zoomAuto = !this.zoomAuto;
          if (this.zoomAuto && this.isResultSelected$.value === true) {
            this.selectResult(this.resultSelected$.getValue());
          }
        }
      },
      {
        id: 'fullExtent',
        title: this.languageService.translate.instant('toastPanel.fullExtent'),
        tooltip: this.languageService.translate.instant(
          'toastPanel.fullExtentTooltip'
        ),
        icon: 'open_in_full',
        display: () => {
          return this.fullExtent$.pipe(map((v) => !v && !this.isDesktop()));
        },
        handler: () => {
          this.fullExtent = true;
        }
      },
      {
        id: 'standardExtent',
        title: this.languageService.translate.instant(
          'toastPanel.standardExtent'
        ),
        tooltip: this.languageService.translate.instant(
          'toastPanel.standardExtentTooltip'
        ),
        icon: 'close_fullscreen',
        display: () => {
          return this.fullExtent$.pipe(map((v) => v && !this.isDesktop()));
        },
        handler: () => {
          this.fullExtent = false;
        }
      }
    ]);
    this.computeFeatureGeoServiceStatus();
    combineLatest([
      this.resultSelected$,
      this.map.layerController.layers$
    ]).subscribe(() => {
      this.computeFeatureGeoServiceStatus();
    });
  }

  ngOnDestroy(): void {
    if (this.resultOrResolution$$) {
      this.resultOrResolution$$.unsubscribe();
    }
    if (this.isSelectedResultOutOfView$$) {
      this.isSelectedResultOutOfView$$.unsubscribe();
    }
    if (this.storageChange$$) {
      this.storageChange$$.unsubscribe();
    }
  }

  private buildResultEmphasis(
    result: SearchResult<Feature>,
    trigger: 'selected' | 'focused' | undefined
  ) {
    this.clearFeatureEmphasis();
    if (!result || (trigger === 'selected' && this.zoomAuto)) {
      return;
    }
    const myOlFeature = featureToOl(result.data, this.map.projection);
    const olGeometry = myOlFeature.getGeometry();
    if (
      featuresAreTooDeepInView(
        this.map.viewController,
        olGeometry.getExtent() as [number, number, number, number],
        0.0025
      )
    ) {
      const extent = olGeometry.getExtent();
      const x = extent[0] + (extent[2] - extent[0]) / 2;
      const y = extent[1] + (extent[3] - extent[1]) / 2;
      const feature1 = new olFeature({
        name: 'abstractFocusedOrSelectedResult',
        geometry: new olPoint([x, y])
      });
      this.abstractFocusedOrSelectedResult = featureFromOl(
        feature1,
        this.map.projection
      );
      this.abstractFocusedOrSelectedResult.meta.style =
        getCommonVectorSelectedStyle(
          Object.assign(
            {},
            { feature: this.abstractFocusedOrSelectedResult },
            trigger === 'selected'
              ? this.queryState.queryOverlayStyleSelection
              : this.queryState.queryOverlayStyleFocus
          )
        );
      this.abstractFocusedOrSelectedResult.meta.style.setZIndex(2000);
      this.map.queryResultsOverlay.addFeature(
        this.abstractFocusedOrSelectedResult,
        FeatureMotion.None
      );
    }
  }

  private clearFeatureEmphasis() {
    if (this.abstractFocusedOrSelectedResult) {
      this.map.queryResultsOverlay.removeFeature(
        this.abstractFocusedOrSelectedResult
      );
      this.abstractFocusedOrSelectedResult = undefined;
    }
  }

  getTitle(result: SearchResult) {
    return getEntityTitle(result);
  }

  focusResult(result: SearchResult<Feature>) {
    this.focusedResult$.next(result);
    this.map.queryResultsOverlay.removeFeature(result.data);

    result.data.meta.style = getCommonVectorSelectedStyle(
      Object.assign(
        {},
        { feature: result.data },
        this.queryState.queryOverlayStyleFocus
      )
    );
    result.data.meta.style.setZIndex(2000);
    this.map.queryResultsOverlay.addFeature(result.data, FeatureMotion.None);
  }

  unfocusResult(result: SearchResult<Feature>, force?) {
    this.focusedResult$.next(undefined);
    if (!force && this.store.state.get(result).focused) {
      return;
    }
    this.map.queryResultsOverlay.removeFeature(result.data);

    result.data.meta.style = getCommonVectorStyle(
      Object.assign(
        {},
        { feature: result.data },
        this.queryState.queryOverlayStyle
      )
    );
    result.data.meta.style.setZIndex(undefined);
    this.map.queryResultsOverlay.addFeature(result.data, FeatureMotion.None);
  }
  getAllowedFieldsAndAlias(layer: AnyLayer): { [key: string]: string } | undefined {
    let allowedFieldsAndAlias;
    if (this.isLayerItem(layer)){
      if (
        (<Layer> layer).options?.source?.options?.sourceFields &&
        (<Layer> layer).options.source.options.sourceFields.length >= 1
      ) {
        allowedFieldsAndAlias = {};
        (<Layer> layer).options.source.options.sourceFields.forEach((sourceField) => {
          const alias = sourceField.alias ? sourceField.alias : sourceField.name;
          allowedFieldsAndAlias[sourceField.name] = alias;
        });
      }
    }
    return allowedFieldsAndAlias;
  }
  findLayerByTitle(title: string, fieldAliases: { [key: string]: string }): AnyLayer | undefined {
    const matchingLayers = this.map.layerController.all.filter(layer => this.isLayerItem(layer) && layer.title === title);

    if (matchingLayers.length === 1) {
      return matchingLayers[0];
    } else if (matchingLayers.length > 1 && fieldAliases && Object.keys(fieldAliases).length > 0) {
      return matchingLayers.find(layer => {
        const allowedFieldsAndAlias = this.getAllowedFieldsAndAlias(layer);
        return allowedFieldsAndAlias && Object.keys(fieldAliases).every(field => field in allowedFieldsAndAlias);
      });
    }

    return undefined;
  }

  //Support wms only
  findLayerByName(layerName: string): AnyLayer | undefined {
    const matchingLayers = this.map.layerController.all.filter(layer =>
      this.isLayerItem(layer) &&
      layer.dataSource.options?.type == 'wms'
      && (layer.dataSource as WMSDataSource).params.LAYERS == layerName);

    if (matchingLayers.length === 1) {
      return matchingLayers[0];
    } else {
      return undefined;
    }
  }

  getDatasourceFields(layerTitle: string, fieldAliases: { [key: string]: string }) {
    const matchedLayer = this.findLayerByTitle(layerTitle, fieldAliases);
    if (matchedLayer) {
      return matchedLayer.dataSource.options.sourceFields;
    } else
      return [];
  }

  getFieldFormats(result: SearchResult<Feature>) {
    let fieldFormats = {};
    if (result.data?.meta?.sourceTitle) {
      const options = this.getDatasourceFields(result.data?.meta?.sourceTitle, result.data?.meta?.alias);
      options?.forEach((sourceField: any) => {
        if (sourceField.format) {
          fieldFormats[sourceField.name] = { type: sourceField.type, format: sourceField.format };
        }
      });
    }

    return fieldFormats;
  }
  formatProperties(result: SearchResult<Feature>) {
    const fieldFormats = this.getFieldFormats(result);
    if (fieldFormats) {
      Object.keys(fieldFormats).forEach((field) => {
        if (fieldFormats[field].type === "date") {
          result.data.properties[field] = formatDate(result.data.properties[field], fieldFormats[field].format, this.locale);
        }
        if (fieldFormats[field].type === "number") {
          result.data.properties[field] = formatNumber(Number(result.data.properties[field]), this.locale, fieldFormats[field].format);
        }
      });
    }
  }

  selectResult(result: SearchResult<Feature>) {
    this.formatProperties(result);
    this.store.state.update(
      result,
      {
        focused: true,
        selected: true
      },
      true
    );
    this.resultSelected$.next(result);
    if (result.data.properties && result.data.properties.target === 'iframe') {
      this.setHtmlDisplay(true);
    } else {
      this.setHtmlDisplay(false);
    }

    const features = [];
    for (const feature of this.store.all()) {
      if (feature.meta.id === result.meta.id) {
        feature.data.meta.style = getCommonVectorSelectedStyle(
          Object.assign(
            {},
            { feature: feature.data },
            this.queryState.queryOverlayStyleSelection
          )
        );
        feature.data.meta.style.setZIndex(2000);
      } else {
        feature.data.meta.style = getCommonVectorStyle(
          Object.assign(
            {},
            { feature: feature.data },
            this.queryState.queryOverlayStyle
          )
        );
      }
      features.push(feature.data);
    }
    this.map.queryResultsOverlay.removeFeatures(features);
    this.map.queryResultsOverlay.addFeatures(features, FeatureMotion.None);

    if (this.zoomAuto) {
      const localOlFeature = this.format.readFeature(
        this.resultSelected$.getValue().data,
        {
          dataProjection: this.resultSelected$.getValue().data.projection,
          featureProjection: this.map.projection
        }
      ) as olFeature;
      moveToOlFeatures(
        this.map.viewController,
        [localOlFeature],
        FeatureMotion.Default
      );
    }

    this.isResultSelected$.next(true);
    this.initialized = false;
  }

  unselectResult() {
    this.resultSelected$.next(undefined);
    this.isResultSelected$.next(false);
    this.setHtmlDisplay(false);
    this.store.state.clear();

    const features = [];
    for (const feature of this.store.all()) {
      feature.data.meta.style = getCommonVectorStyle(
        Object.assign(
          {},
          { feature: feature.data },
          this.queryState.queryOverlayStyle
        )
      );
      features.push(feature.data);
    }
    this.map.queryResultsOverlay.setFeatures(
      features,
      FeatureMotion.None,
      'map'
    );
  }

  handleWksSelection() {
    const entities = this.store.entities$.getValue();
    const layersTitle = [...new Set(entities.map((e) => e.source.title))];
    const workspaces = this.workspaceState.store.entities$.getValue();
    if (workspaces.length) {
      const wksToHandle = workspaces.filter((wks) =>
        layersTitle.includes(wks.title)
      );
      wksToHandle.map((ws) => {
        ws.entityStore.state.updateMany(ws.entityStore.view.all(), {
          selected: false
        });
      });
    }
  }

  clear() {
    this.handleWksSelection();
    this.clearFeatureEmphasis();
    this.map.queryResultsOverlay.clear();
    this.store.clear();
    this.unselectResult();
    this.setHtmlDisplay(false);
  }

  isMobile(): boolean {
    return this.mediaService.getMedia() === Media.Mobile;
  }
  isDesktop(): boolean {
    return this.mediaService.isDesktop();
  }

  handleKeyboardEvent(event) {
    if (event.keyCode === 37) {
      this.previousResult();
    } else if (event.keyCode === 39) {
      this.nextResult();
    }
  }

  previousResult() {
    if (!this.resultSelected$.value) {
      return;
    }
    let i = this.results.indexOf(this.resultSelected$.value);
    const previousResult = this.results[--i];
    if (previousResult) {
      this.selectResult(previousResult);
    }
  }

  nextResult() {
    if (!this.resultSelected$.value) {
      return;
    }
    let i = this.results.indexOf(this.resultSelected$.value);
    const nextResult = this.results[++i];
    if (nextResult) {
      this.selectResult(nextResult);
    }
  }

  hasGeoService() {
    return this.getGeoServices().length;
  }

  private getGeoServices(): ExtendedGeoServiceDefinition[] {
    const resultSelected = this.resultSelected$.getValue();
    if (!resultSelected) {
      return [];
    }
    const hasGeoServiceProperties: ExtendedGeoServiceDefinition[] = [];
    const keys = Object.keys(resultSelected.data.properties);
    Object.entries(resultSelected.data.properties).forEach((entry) => {
      const [key, value] = entry;
      const geoService = this.propertyTypeDetectorService.getGeoService(
        value,
        keys
      );
      const extendedGeoService: ExtendedGeoServiceDefinition = Object.assign(
        {},
        geoService,
        { propertyForUrl: undefined }
      );
      if (geoService) {
        extendedGeoService.propertyForUrl = key;
        hasGeoServiceProperties.push(extendedGeoService);
      }
    });
    return hasGeoServiceProperties;
  }

  handleLayer() {
    const layersIds = this.map.layerController.all.filter(layer => this.isLayerItem(layer)).map((layer) => layer.id);
    let potententialLayerToAdd = this.potententialLayerToAdd$.getValue();
    if (!potententialLayerToAdd) {
      this.computeFeatureGeoServiceStatus();
    }
    potententialLayerToAdd = this.potententialLayerToAdd$.getValue();

    if (layersIds.includes(potententialLayerToAdd.id)) {
      const layerToRemove = this.map.layerController.getById(
        potententialLayerToAdd.id
      );
      if (layerToRemove) {
        this.map.layerController.remove(layerToRemove);
        this.potententialLayerisAdded$.next(false);
      }
    } else {
      this.layerService
        .createAsyncLayer(potententialLayerToAdd.sourceOptions)
        .subscribe((layer) => {
          this.map.layersAddedByClick$.next([layer]);
          this.map.layerController.add(layer);
          this.potententialLayerisAdded$.next(true);
        });
    }
  }

  private computeFeatureGeoServiceStatus() {
    const resultSelected = this.resultSelected$.getValue();
    if (!resultSelected) {
      return;
    }
    const geoServices = this.getGeoServices();
    if (geoServices.length) {
      const firstGeoService = geoServices[0];
      const so = this.computeSourceOptionsFromProperties(
        resultSelected.data.properties,
        firstGeoService
      );
      const soId = generateIdFromSourceOptions(so.sourceOptions);
      this.potententialLayerToAdd$.next({ id: soId, sourceOptions: so });
      const layersIds = this.map.layerController.all.filter(l => this.isLayerItem(l)).map((l) => l.id);
      this.potententialLayerisAdded$.next(
        layersIds.includes(soId) ? true : false
      );
    }
  }

  private computeSourceOptionsFromProperties(
    properties: unknown,
    geoService: ExtendedGeoServiceDefinition
  ) {
    const keys = Object.keys(properties);
    const propertiesForLayerName = keys.filter((p) =>
      geoService.propertiesForLayerName.includes(p)
    );
    // providing the the first matching regex;
    const layerName = properties[propertiesForLayerName[0]];
    const url = properties[geoService.propertyForUrl];
    let appliedLayerName = layerName;
    let arcgisLayerName = undefined;
    if (
      ['arcgisrest', 'imagearcgisrest', 'tilearcgisrest'].includes(
        geoService.type
      )
    ) {
      arcgisLayerName = layerName;
      appliedLayerName = undefined;
    }
    const so = ObjectUtils.removeUndefined({
      sourceOptions: {
        type: geoService.type || 'wms',
        url,
        optionsFromCapabilities: true,
        optionsFromApi: true,
        params: {
          LAYERS: appliedLayerName,
          LAYER: arcgisLayerName
        }
      }
    });
    return so;
  }

  zoomTo() {
    const localOlFeature = this.format.readFeature(
      this.resultSelected$.getValue().data,
      {
        dataProjection: this.resultSelected$.getValue().data.projection,
        featureProjection: this.map.projection
      }
    ) as olFeature;
    moveToOlFeatures(
      this.map.viewController,
      [localOlFeature],
      FeatureMotion.Zoom
    );
  }

  swipe(action: string) {
    if (action === ToastPanelComponent.SWIPE_ACTION.RIGHT) {
      this.previousResult();
    } else if (action === ToastPanelComponent.SWIPE_ACTION.LEFT) {
      this.nextResult();
    } else if (action === ToastPanelComponent.SWIPE_ACTION.UP) {
      this.opened = true;
    } else if (action === ToastPanelComponent.SWIPE_ACTION.DOWN) {
      this.opened = false;
    }
  }

  onToggleClick(e: MouseEvent) {
    if ((e.target as any).className !== 'igo-panel-title') {
      return;
    }
    this.opened = !this.opened;
  }

  /**
   * Invoke the action handler
   * @internal
   */
  onTriggerAction(action: Action) {
    const args = action.args || [];
    action.handler(...args);
  }

  setHtmlDisplay(value: boolean) {
    if (value === true) {
      this.isHtmlDisplay = true;
      this.windowHtmlDisplayEvent.emit(true);
    } else {
      this.isHtmlDisplay = false;
      this.windowHtmlDisplayEvent.emit(false);
    }
  }

  isHtmlAndDesktop(): boolean {
    if (this.isHtmlDisplay && this.isDesktop()) {
      return true;
    } else {
      return false;
    }
  }

  setResizeWindowIcon() {
    if (this.fullExtent) {
      this.iconResizeWindows = 'arrow-collapse';
      // this.iconResizeWindows = 'vector-arrange-below';
    } else {
      this.iconResizeWindows = 'arrow-expand';
      // this.iconResizeWindows = 'crop-square';
    }
  }

  resizeWindows() {
    this.storageService.set('fullExtent', !this.fullExtent);

    if (this.fullExtent) {
      this.reduceWindow();
    } else {
      this.enlargeWindows();
    }
  }

  reduceWindow() {
    this.fullExtent = false;
    this.setResizeWindowIcon();
  }

  enlargeWindows() {
    this.fullExtent = true;
    this.setResizeWindowIcon();
  }

  getRdeLayerInfo(result:SearchResult<Feature<Record<string, any>>>): RdeSearchLayer {
    if ((<any> result.data)?.id == undefined) {
      return undefined;
    }
    const layerName = (<any> result.data)?.id.toString().split(".")[0];
    const rdeSearchLayer = this.rdeSearchLayers.filter(l=>l.wmsLayer.includes(layerName));
    if (rdeSearchLayer.length > 0) {
      return rdeSearchLayer[0];
    } else
      return undefined;
  }
  rechercherRde(rdeLayer: RdeSearchLayer, key:string):void {
    const messagesDeValidation: { [key: string]: string } = {
      listeVide: Constantes.MessageApplicatif.AucunElement_6I,
      erreurCouche:Constantes.MessageApplicatif.CoucheIndisponible_16T
    };

    const indicateurHistorique = false;
    const indicateurConfidentiel = false;
    switch(rdeLayer.rdeSearchType){
      case "DroitContrainte":
        const statut = "AC";

        const numeroDroitContrainte = key;
        const dcLayerid : string = 'RDEDroitsContaintes';
        const dcHistoryLayerid : string = 'RDEDroitsContraintesHistorique';
        this.rechercheRdeService.obtenirListeDroitsContraintes(
          statut,
          indicateurHistorique,
          indicateurConfidentiel,
          numeroDroitContrainte)
        .subscribe({
          next: reponse => {
            if (reponse?.length > 0){
              this.rechercheRdeService.addSearchResultsToMap(
                dcLayerid,
                this.rechercheRdeService.formatFeaturesForTable('droitContraintes', reponse),
                this.cdRef,
                this.rechercheRdeService.getEntityTableTemplate('droitContraintes',
                  this.getActionHandlers('droitContraintes', false, dcHistoryLayerid,
                    this.rechercheRdeService.getEntityTableTemplate('droitContraintes', this.getActionHandlers('droitContraintes', true)))),
                undefined
              )
            } else {
              this.messageService.info(messagesDeValidation.listeVide);
            }
          },
          error: error => {
            this.messageService.error(messagesDeValidation.erreurCouche);
            console.log(error);
          }
        });
        break;
      case "TransactionProprieteAmendement":
        const numeroTPA = key;
        const statutTransaction = "TR";
        const statutTraitement = "TM";
        const tpaLayerid: string = 'RDETransactionsProprietesAmendements';
        const tpaHistoryLayerid: string = 'RDETransactionsProprietesAmendementsHistorique';
        this.rechercheRdeService.obtenirListeTransactionsProprietesAmendements(
          statutTransaction,
          statutTraitement,
          indicateurHistorique,
          numeroTPA
        )
        .subscribe({
          next: reponse => {
            if (reponse?.length > 0){
              this.rechercheRdeService.addSearchResultsToMap(
                tpaLayerid,
                this.rechercheRdeService.formatFeaturesForTable('transactionsAmendements', reponse),
                this.cdRef,
                this.rechercheRdeService.getEntityTableTemplate('transactionsAmendements',
                  this.getActionHandlers('transactionsAmendements', false, tpaHistoryLayerid,
                    this.rechercheRdeService.getEntityTableTemplate('transactionsAmendements', this.getActionHandlers('transactionsAmendements', true)))),
                undefined
              )
            } else {
              this.messageService.info(messagesDeValidation.listeVide);
            }
          },
          error: error => {
            this.messageService.error(messagesDeValidation.erreurCouche);
            console.log(error);
          }
        });
        break;
      case "TransfertAutorite":
        const numeroTA = +key;
        const statutTA = ["IN", "CO"];
        const taLayerid: string = 'RDETransfertAutorite';
        this.rechercheRdeService.obtenirListeTransfertsAutorite(
          statutTA,
          numeroTA
        )
        .subscribe({
          next: reponse => {
            if (reponse?.length > 0){
              this.rechercheRdeService.addSearchResultsToMap(
                taLayerid,
                this.rechercheRdeService.formatFeaturesForTable('transfertAutorite',reponse),
                this.cdRef,
                this.rechercheRdeService.getEntityTableTemplate('transfertAutorite',
                  this.getActionHandlers('transfertAutorite', false)),
                undefined
              )
            } else {
              this.messageService.info(messagesDeValidation.listeVide);
            }
          },
          error: error => {
            this.messageService.error(messagesDeValidation.erreurCouche);
            console.log(error);
          }
        });
        break;
    }
  }
  getActionHandlers(
    context:RdeContext,
    isHistorical?:boolean,
    historyLayerid?: string,
    templateHistory?: EntityTableTemplate
  ) : Record<string,(row: any, record?: EntityRecord<object>) => void > {
    const messagesDeValidation: { [key: string]: string } = {
      listeVide: Constantes.MessageApplicatif.AucunElement_6I,
      erreurCouche:Constantes.MessageApplicatif.CoucheIndisponible_16T
    };
    switch(context){
      case 'droitContraintes':
        if (!isHistorical) {
        return {
          details: (row, record) => {
            if (record?.state?.selected) {
              this.rechercheRdeService.obtenirFicheDescriptive(row).subscribe({
                next: (blob: Blob) => {
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'Fiche - Droits et contraintes.pdf';
                  a.click();
                  window.URL.revokeObjectURL(url); // Clean up
                },
                error: (err) => {
                  console.error('Erreur lors du téléchargement du PDF', err);
                }
              });
            }
          },

          getHistoricalData: (row, record) => {
            if (record?.state?.selected) {
              this.rechercheRdeService.obtenirHistorique(row).subscribe({
                next: (history:DroitContrainteDto[]) => {
                if(history.length > 0 ){
                  this.rechercheRdeService.addSearchResultsToMap(
                      historyLayerid,
                      this.rechercheRdeService.formatFeaturesForTable('droitContraintes', history, true),
                      this.cdRef,
                      templateHistory,
                      undefined
                    )
                  } else {
                    this.messageService.info(messagesDeValidation.listeVide);
                  }
                },
                error: (err) => {
                  console.error('Erreur lors de la reception historique', err);
                }
              });
            }
          }
        };
        } else {
          return {
            details: (row, record) => {
              if (record?.state?.selected) {
                this.rechercheRdeService.obtenirFicheDescriptive(row, true).subscribe({
                  next: (blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'Fiche - Droits et contraintes.pdf';
                    a.click();
                    window.URL.revokeObjectURL(url); // Clean up
                  },
                  error: (err) => {
                    console.error('Erreur lors du téléchargement du PDF', err);
                  }
                });
              }
            }
          };
        }
      case 'transactionsAmendements':
        if(!isHistorical) {
          return {
            details: (row, record) => {
              if (record?.state?.selected) {
                this.rechercheRdeService.obtenirFicheDescriptiveTPA(row).subscribe({
                  next: (blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'Fiche - Transactions de propriété et amendements.pdf';
                    a.click();
                    window.URL.revokeObjectURL(url); // Clean up
                  },
                  error: (err) => {
                    console.error('Erreur lors du téléchargement du PDF', err);
                  }
                });
              }
            },

            getHistoricalData: (row, record) => {
              if (record?.state?.selected) {
                this.rechercheRdeService.obtenirHistoriqueTPA(row).subscribe({
                  next: (history:TransactionProprieteAmendementDto[]) => {
                  if(history.length > 0 ){
                    this.rechercheRdeService.addSearchResultsToMap(
                        historyLayerid,
                        this.rechercheRdeService.formatFeaturesForTable('transactionsAmendements', history, true),
                        this.cdRef,
                        templateHistory,
                        undefined
                      )
                    } else {
                      this.messageService.info(messagesDeValidation.listeVide);
                    }
                  },
                  error: (err) => {
                    console.error('Erreur lors de la reception historique', err);
                  }
                });
              }
            }
          };
        } else {
          return {
            details: (row, record) => {
              if (record?.state?.selected) {
                this.rechercheRdeService.obtenirFicheDescriptiveTPA(row, true).subscribe({
                  next: (blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'Fiche - Transactions de propriété et amendements.pdf';
                    a.click();
                    window.URL.revokeObjectURL(url); // Clean up
                  },
                  error: (err) => {
                    console.error('Erreur lors du téléchargement du PDF', err);
                  }
                });
              }
            }
          };
        }
      case 'transfertAutorite':
        return {
            details: (row, record) => {
              if (record?.state?.selected) {
                this.rechercheRdeService.obtenirFicheDescriptiveTA(row).subscribe({
                  next: (blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'Fiche - Transfert Autorité.pdf';
                    a.click();
                    window.URL.revokeObjectURL(url); // Clean up
                  },
                  error: (err) => {
                    console.error('Erreur lors du téléchargement du PDF', err);
                  }
                });
              }
            }
          }
    }
  }
}
